import * as React from 'react';

import {
  Badge,
  ExternalIcon,
  formatDate,
  InfoIcon,
  Skeleton,
  Tooltip,
} from '@setel/portal-ui';
import { environments } from 'environments/environment';
import { DescItem } from '@setel/portal-ui';
import { CustomDescList } from 'modules/accounts/account-details';
import { useUserAttributes } from '../user-attributes.queries';
import {
  EKYC_STATUS,
  IUserAttribute,
} from 'shared/interfaces/user-attributes.interface';
import { useGetUserById } from 'modules/accounts/accounts.queries';
import { useEffect, useState } from 'react';

const statusColorMap: Record<EKYC_STATUS, any> = {
  PENDING: 'lemon',
  APPROVED: 'success',
  REJECTED: 'error',
};

export function UserAttributes({ userId }: { userId: string }) {
  const [registerDate, setRegisterDate] = useState<string | undefined>(
    undefined
  );
  const { data } = useUserAttributes(userId);
  const userProfile = useGetUserById(userId);

  const accountStatus = (attributes: IUserAttribute) => {
    const accountStatusArray = [];
    if (
      attributes.attributes.vip_account &&
      attributes.attributes.vip_account.value
    ) {
      accountStatusArray.push('VIP');
    }
    if (
      attributes.attributes.account_attribute_tag_clv_segment &&
      attributes.attributes.account_attribute_tag_clv_segment.value ===
      'champions'
    ) {
      accountStatusArray.push('Champions');
    }
    if (
      attributes.attributes.account_attribute_tag_clv_clv_1month &&
      attributes.attributes.account_attribute_tag_clv_clv_1month.value >= 500
    ) {
      accountStatusArray.push('Top tier');
    }
    return accountStatusArray.length ? accountStatusArray.join(', ') : '-';
  };

  useEffect(() => {
    if (data?.attributes?.account_register_complete_date?.value) {
      setRegisterDate(data.attributes.account_register_complete_date.value);
      return;
    }
    if (userProfile) {
      setRegisterDate(userProfile.data?.createdAt?.toString());
      return;
    }
  }, [data, userProfile]);

  return (
    <>
      <div className="border-gray-200 border">
        <React.Fragment>
          <CustomDescList>
            <DescItem
              valueClassName="ml-3 flex align-items-center"
              label="eKYC status"
              value={
                data ? (
                  <Badge
                    color={
                      data?.attributes
                        ?.compliance_control_verification_ekyc_status?.value
                        ? statusColorMap[
                        data?.attributes
                          ?.compliance_control_verification_ekyc_status
                          ?.value
                        ]
                        : 'grey'
                    }
                    rounded="rounded"
                    size="small"
                  >
                    {data?.attributes
                      ?.compliance_control_verification_ekyc_status?.value ||
                      'NOT FOUND'}
                  </Badge>
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
            <DescItem
              labelClassName="mt-3"
              valueClassName="mt-3 ml-3 flex align-items-center"
              label="VIP criteria"
              value={
                data ? (
                  accountStatus(data)
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
            <DescItem
              labelClassName="mt-3"
              valueClassName="mt-3 ml-3 flex align-items-center"
              label="Lifetime fuel order value"
              value={
                data ? (
                  data?.attributes?.fuel_order_lifetime_value ? (
                    `RM${Math.round(
                      data?.attributes?.fuel_order_lifetime_value?.value * 100
                    ) / 100
                    }`
                  ) : (
                    '-'
                  )
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
            <DescItem
              labelClassName="mt-3"
              valueClassName="mt-3 ml-3 flex align-items-center"
              label="Lifetime fuel success count"
              value={
                data ? (
                  data?.attributes?.fuel_order_lifetime_success_count?.value ||
                  '-'
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
            <DescItem
              labelClassName="mt-3"
              valueClassName="mt-3 ml-3 flex align-items-center"
              label={
                <div className="flex items-center">
                  Last NPS rating
                  <Tooltip label="Submitted after user completed their fuel purchase.">
                    <InfoIcon className="text-gray-400 h-4 w-4 ml-1" />
                  </Tooltip>
                </div>
              }
              value={
                data ? (
                  `${data?.attributes?.fuel_order_last_nps_rating?.value || '-'
                  }/10`
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
            <DescItem
              labelClassName="mt-3"
              valueClassName="mt-3 ml-3 flex align-items-center"
              label="Lifetime store order value"
              value={
                data ? (
                  data?.attributes?.store_order_lifetime_value ? (
                    `RM${Math.round(
                      data?.attributes?.store_order_lifetime_value?.value *
                      100
                    ) / 100
                    }`
                  ) : (
                    '-'
                  )
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
            <DescItem
              labelClassName="mt-3"
              valueClassName="mt-3 ml-3 flex align-items-center"
              label="Register date"
              value={
                data ? (
                  registerDate ? (
                    formatDate(registerDate)
                  ) : (
                    '-'
                  )
                ) : (
                  <Skeleton className="animate-pulse" width="medium" />
                )
              }
            />
          </CustomDescList>
          <hr className="mx-4" />
          <a
            rel="noreferrer"
            href={`${environments.webAdminUrl}/accounts/${userId}`}
            className="text-brand-500 tracking-1 font-semibold my-4 text-center flex justify-center"
            target="_blank"
          >
            <span className="text-xs">VIEW MORE </span>
            <ExternalIcon className="ml-2 inline" />
          </a>
        </React.Fragment>
      </div>
    </>
  );
}
